<template>
    <div :class="[marginClass || (label ? 'mb-3' : 'mb-0'), customClass]" ref="root" class="material-multiselect">
        <label v-if="label" class="form-label fw-semibold text-xs text-dark mb-1 d-flex align-items-center gap-1">
            <i v-if="icon" class="material-icons text-xs text-secondary">{{ icon }}</i>
            {{ label }}
        </label>

        <div class="dropdown multiselect-dropdown position-relative">
            <button
                class="multiselect-trigger btn btn-outline-secondary w-100 text-start d-flex align-items-center justify-content-between"
                :class="[getTriggerClasses, { 'has-value': selectedCount > 0, 'is-open': showDropdown }]"
                type="button"
                :aria-expanded="showDropdown"
                :disabled="disabled"
                @click="toggleDropdown"
            >
                <div class="d-flex align-items-center text-truncate pe-2">
                    <span v-if="selectedCount === 0" class="text-muted font-weight-normal text-truncate">{{ placeholder }}</span>
                    <span v-else class="selected-label text-dark font-weight-bold text-truncate">{{ displayLabel }}</span>
                </div>

                <div class="d-flex align-items-center ms-auto flex-shrink-0 gap-1">
                    <span v-if="selectedCount > 0" class="badge-count">{{ selectedCount }}</span>
                    <i class="material-icons text-sm transition-transform" :class="{ 'rotate-180': showDropdown }">expand_more</i>
                </div>
            </button>

            <div class="dropdown-menu multiselect-menu w-100 p-2 shadow-lg" :class="{ show: showDropdown }">
                <!-- Search bar -->
                <div class="search-container mb-2">
                    <div class="input-group input-group-sm">
                        <span class="input-group-text bg-white border-end-0 py-1 ps-2 pe-1 text-muted">
                            <i class="material-icons text-sm">search</i>
                        </span>
                        <input
                            type="text"
                            class="form-control form-control-sm border-start-0 ps-1"
                            placeholder="Search..."
                            v-model="search"
                            @keydown.stop
                        />
                        <button
                            v-if="search"
                            type="button"
                            class="btn btn-sm btn-link text-muted p-0 px-2 m-0 border-0 shadow-none"
                            @click="search = ''"
                        >
                            <i class="material-icons text-xs">close</i>
                        </button>
                    </div>
                </div>

                <!-- Select All / Clear toolbar -->
                <div class="d-flex justify-content-between align-items-center mb-2 px-1 text-xs">
                    <span class="text-muted font-weight-normal">
                        {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'option' : 'options' }}
                    </span>
                    <div class="d-flex gap-2">
                        <button
                            v-if="allowSelectAll && filteredItems.length > 0"
                            type="button"
                            class="btn-text-action text-primary"
                            @click="selectAll"
                        >
                            Select all
                        </button>
                        <button
                            v-if="allowSelectAll && selectedCount > 0"
                            type="button"
                            class="btn-text-action text-danger"
                            @click="deselectAll"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                <!-- Items list -->
                <div class="multiselect-items-list">
                    <div v-if="filteredItems.length === 0" class="text-muted small py-3 text-center">
                        <i class="material-icons text-md d-block mb-1 text-secondary">search_off</i>
                        No matching items
                    </div>

                    <div
                        v-for="item in filteredItems"
                        :key="`${instanceId}-${getItemId(item)}`"
                        class="multiselect-item"
                        :class="{ 'is-selected': isItemSelected(item) }"
                        role="option"
                        :aria-selected="isItemSelected(item)"
                        @click.stop="toggleItem(item)"
                    >
                        <input
                            class="form-check-input my-0 me-2"
                            type="checkbox"
                            tabindex="-1"
                            :id="`${instanceId}-item-${getItemId(item)}`"
                            :checked="isItemSelected(item)"
                            @click.prevent.stop
                            @change.prevent.stop
                        />
                        <div class="form-check-label mb-0 flex-grow-1 text-truncate">
                            <slot name="item" :item="item">
                                <span class="item-main-label text-sm">{{ getItemLabel(item) }}</span>
                                <small v-if="getItemSubLabel(item)" class="item-sub-label text-muted d-block text-xs">{{ getItemSubLabel(item) }}</small>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Selected Badges (optional) -->
        <div class="mt-2" v-if="selectedCount > 0 && showSelectedOptions">
            <span
                v-for="(it) in visibleSelected"
                :key="`${instanceId}-badge-${getItemId(it)}`"
                class="badge bg-info me-2 mb-2 px-2 py-1 d-inline-flex align-items-center rounded-pill"
            >
                <span class="me-1">{{ getItemLabel(it) }}</span>
                <button
                    type="button"
                    class="text-xs text-white btn-close btn-close-white"
                    aria-label="Remove"
                    style="width: 0.75rem; height: 0.75rem; font-size: 0.6rem;"
                    @click.stop="removeById(getItemId(it))"
                ></button>
            </span>

            <span v-if="selectedCount > maxVisibleBadges" class="text-muted small align-middle">
                +{{ selectedCount - maxVisibleBadges }} more
            </span>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

/**
 * Generic MaterialMultiSelect
 */
const props = defineProps({
    id: { type: String, default: null },
    items: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    textValue: { type: String, default: "" },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "Select..." },
    icon: { type: String, default: "" },
    itemTrackBy: { type: [String, Function], default: "id" },
    itemLabel: { type: [String, Function], default: (item) => (typeof item === "object" ? item.name ?? String(item) : String(item)) },
    itemSubLabel: { type: [String, Function, null], default: null },
    emitAs: { type: String, default: "object" }, // 'object' or 'id'
    searchKeys: { type: Array, default: null }, // if null search label/subLabel
    allowSelectAll: { type: Boolean, default: true },
    showTextField: { type: Boolean, default: false },
    textFieldPlaceholder: { type: String, default: "Enter text..." },
    maxVisibleBadges: { type: Number, default: 5 },
    showSelectedOptions: { type: Boolean, default: true },
    marginClass: { type: String, default: "" },
    customClass: { type: String, default: "" },
    size: { type: String, default: "default" },
    disabled: { type: Boolean, default: false }
});

const emit = defineEmits(["update:modelValue", "update:textValue"]);

/* Generate unique instance ID */
let instanceIdCounter = 0;
const instanceId = props.id || `multi-select-${++instanceIdCounter}-${Date.now()}`;

/* UI state */
const showDropdown = ref(false);
const search = ref("");
const root = ref(null);

/* helper accessors */
function getItemId(item) {
    if (typeof props.itemTrackBy === "function") return props.itemTrackBy(item);
    if (typeof item === "object" && item !== null && props.itemTrackBy in item) return item[props.itemTrackBy];
    return item;
}

function getItemLabel(item) {
    if (!item) return "";
    if (typeof props.itemLabel === "function") return props.itemLabel(item);
    if (typeof item === "object" && item !== null && props.itemLabel in item) return item[props.itemLabel];
    return String(item);
}

function getItemSubLabel(item) {
    if (!props.itemSubLabel || !item) return "";
    if (typeof props.itemSubLabel === "function") return props.itemSubLabel(item);
    if (typeof item === "object" && item !== null && props.itemSubLabel in item) return item[props.itemSubLabel];
    return "";
}

/* selectedIds internal representation */
const selectedIds = ref([]);
function initializeSelectedIds() {
    const mv = props.modelValue ?? [];
    if (props.emitAs === "id") {
        selectedIds.value = Array.isArray(mv) ? [...mv] : [];
    } else {
        if (Array.isArray(mv)) selectedIds.value = mv.map((o) => getItemId(o));
        else selectedIds.value = [];
    }
}
initializeSelectedIds();

/* keep synced if parent changes modelValue */
watch(
    () => props.modelValue,
    () => {
        const mv = props.modelValue ?? [];
        let newIds;
        if (props.emitAs === "id") {
            newIds = Array.isArray(mv) ? [...mv] : [];
        } else {
            newIds = Array.isArray(mv) ? mv.map((o) => getItemId(o)) : [];
        }
        const currentSorted = [...selectedIds.value].map(String).sort();
        const newSorted = [...newIds].map(String).sort();
        if (JSON.stringify(currentSorted) !== JSON.stringify(newSorted)) {
            selectedIds.value = newIds;
        }
    },
    { deep: true }
);

/* selected items computed */
const selectedItems = computed(() => {
    return props.items.filter((it) => {
        const id = getItemId(it);
        return selectedIds.value.some((sId) => String(sId) === String(id));
    });
});

const selectedCount = computed(() => selectedIds.value.length);
const visibleSelected = computed(() => selectedItems.value.slice(0, props.maxVisibleBadges));

const displayLabel = computed(() => {
    if (selectedCount.value === 0) return props.placeholder;
    if (selectedCount.value === 1 && selectedItems.value.length > 0) {
        return getItemLabel(selectedItems.value[0]);
    }
    if (selectedCount.value === 2 && selectedItems.value.length >= 2) {
        return `${getItemLabel(selectedItems.value[0])}, ${getItemLabel(selectedItems.value[1])}`;
    }
    if (selectedItems.value.length > 0) {
        return `${getItemLabel(selectedItems.value[0])} (+${selectedCount.value - 1} more)`;
    }
    return `${selectedCount.value} selected`;
});

const getTriggerClasses = computed(() => {
    if (props.size === "sm") return "py-1 px-2 text-xs";
    if (props.size === "lg") return "py-2 px-3 text-base";
    return "py-2 px-3 text-sm";
});

/* filtered items (search) */
const filteredItems = computed(() => {
    const q = search.value.trim().toLowerCase();
    if (!q) return props.items;
    const keys = props.searchKeys;
    return props.items.filter((it) => {
        if (keys && keys.length) {
            return keys.some((k) => {
                const v = typeof k === "function" ? k(it) : (it && it[k]);
                return String(v ?? "").toLowerCase().includes(q);
            });
        }
        const label = getItemLabel(it) ?? "";
        const sub = getItemSubLabel(it) ?? "";
        return String(label).toLowerCase().includes(q) || String(sub).toLowerCase().includes(q);
    });
});

/* Item selection helpers */
function isItemSelected(item) {
    const id = getItemId(item);
    return selectedIds.value.some((sId) => String(sId) === String(id));
}

function toggleItem(item) {
    const id = getItemId(item);
    if (isItemSelected(item)) {
        removeById(id);
    } else {
        selectedIds.value = [...selectedIds.value, id];
    }
}

/* update parent when selectedIds change */
watch(
    selectedIds,
    (newIds) => {
        if (props.emitAs === "id") {
            emit("update:modelValue", [...newIds]);
        } else {
            const objs = props.items.filter((it) => {
                const id = getItemId(it);
                return newIds.some((sId) => String(sId) === String(id));
            });
            emit("update:modelValue", objs);
        }
    },
    { deep: true }
);

/* actions */
function toggleDropdown() {
    if (!props.disabled) {
        showDropdown.value = !showDropdown.value;
    }
}

function selectAll() {
    const allIds = filteredItems.value.map((it) => getItemId(it));
    const merged = Array.from(new Set([...selectedIds.value, ...allIds]));
    selectedIds.value = merged;
}

function deselectAll() {
    if (search.value.trim()) {
        const filteredItemIds = filteredItems.value.map((it) => String(getItemId(it)));
        selectedIds.value = selectedIds.value.filter((id) => !filteredItemIds.includes(String(id)));
    } else {
        selectedIds.value = [];
    }
}

function removeById(id) {
    selectedIds.value = selectedIds.value.filter((i) => String(i) !== String(id));
}

/* click outside to close */
function onDocClick(e) {
    if (!root.value) return;
    if (!root.value.contains(e.target)) showDropdown.value = false;
}
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<style scoped>
.material-multiselect {
    position: relative;
    width: 100%;
}

.multiselect-trigger {
    background-color: #fff;
    border: 1px solid #d2d6da;
    border-radius: 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: none !important;
    letter-spacing: 0;
    color: #344767;
    transition: all 0.2s ease;
    min-height: 40px;
    box-shadow: none !important;
    margin-bottom: 0;
}

.multiselect-trigger:hover {
    background-color: #fff;
    border-color: #667eea;
    color: #344767;
}

.multiselect-trigger.is-open {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2) !important;
}

.multiselect-trigger.has-value {
    border-color: #a3b1d1;
    background-color: #fcfdff;
}

.badge-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 7px;
    min-width: 18px;
    line-height: 1;
}

.transition-transform {
    transition: transform 0.2s ease;
}

.rotate-180 {
    transform: rotate(180deg);
}

.multiselect-menu {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 4px !important;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    z-index: 1060;
    min-width: 240px;
    max-width: 400px;
}

.search-container .input-group {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
}

.search-container input:focus {
    box-shadow: none;
}

.btn-text-action {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
}

.btn-text-action:hover {
    opacity: 0.8;
}

.multiselect-items-list {
    max-height: 220px;
    overflow-y: auto;
    scrollbar-width: thin;
}

.multiselect-item {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    user-select: none;
}

.multiselect-item:hover {
    background-color: #f1f5f9;
}

.multiselect-item.is-selected {
    background-color: #eef2ff;
}

.multiselect-item .form-check-input {
    cursor: pointer;
    border-color: #a0aec0;
}

.multiselect-item .form-check-input:checked {
    background-color: #667eea;
    border-color: #667eea;
}

.multiselect-item .form-check-label {
    cursor: pointer;
    pointer-events: none;
}

.item-main-label {
    color: #2d3748;
    font-weight: 500;
}

.badge.bg-info {
    background-color: #17c1e8 !important;
    color: #fff;
}
</style>
